import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { ArrowRight, Fuel, HardHat, Wheat, Truck, ChevronRight, Quote, ChevronLeft } from 'lucide-react'
import { company, services, coreValues, testimonials } from '@data/company'
import AnimatedSection from '@components/AnimatedSection'

const iconMap: Record<string, React.ElementType> = {
  Fuel, HardHat, Wheat, Truck
}

interface HeroSlide {
  badge: string
  headlineTop: string
  headlineBottom: string
  description: string
  ctaPrimary: { label: string; to: string }
  ctaSecondary: { label: string; to: string }
  accentClass: string
  badgeBg: string
  badgeBorder: string
  dotPulse: string
  image: string
  imageAlt: string
  placeholderGrad: string
}

const heroSlides: HeroSlide[] = [
  {
    badge: `Established ${company.founded} — Proudly Nigerian`,
    headlineTop: 'Fuelling Growth.',
    headlineBottom: 'Powering Nations.',
    description: `${company.description} Driving excellence across energy, construction, agriculture, and logistics since ${company.founded}.`,
    ctaPrimary: { label: 'Explore Our Services', to: '/services' },
    ctaSecondary: { label: 'Partner With Us', to: '/contact' },
    accentClass: 'text-martimma-red',
    badgeBg: 'bg-martimma-red/10',
    badgeBorder: 'border-martimma-red/20',
    dotPulse: 'bg-martimma-red',
    image: '/images/hero-brand.jpg',
    imageAlt: 'Martimma Global Services integrated operations',
    placeholderGrad: 'from-[#1a0a0a] via-martimma-dark to-martimma-dark-light',
  },
  {
    badge: 'Petroleum Products · Supply & Distribution',
    headlineTop: 'Powering Nigeria',
    headlineBottom: 'One Drop at a Time.',
    description: "Reliable supply of PMS, AGO, DPK, ATK, LPFO, lubricants and retail service stations — keeping Nigeria's economy energised around the clock.",
    ctaPrimary: { label: 'Energy Solutions', to: '/services' },
    ctaSecondary: { label: 'Our Infrastructure', to: '/infrastructure' },
    accentClass: 'text-martimma-red',
    badgeBg: 'bg-martimma-red/10',
    badgeBorder: 'border-martimma-red/20',
    dotPulse: 'bg-martimma-red',
    image: '/images/petroleum-hero.jpg',
    imageAlt: 'Petroleum supply and distribution operations',
    placeholderGrad: 'from-[#200808] via-martimma-dark to-martimma-dark-light',
  },
  {
    badge: 'Construction · Engineering & Build',
    headlineTop: "Building Tomorrow's",
    headlineBottom: 'Infrastructure Today.',
    description: 'From roads and bridges to tank farms and industrial facilities — delivering durable, world-class construction across every corner of Nigeria.',
    ctaPrimary: { label: 'Construction Services', to: '/services' },
    ctaSecondary: { label: 'About Us', to: '/about' },
    accentClass: 'text-martimma-yellow',
    badgeBg: 'bg-martimma-yellow/10',
    badgeBorder: 'border-martimma-yellow/20',
    dotPulse: 'bg-martimma-yellow',
    image: '/images/construction-hero.jpg',
    imageAlt: 'Construction and engineering projects across Nigeria',
    placeholderGrad: 'from-[#1a1400] via-martimma-dark to-martimma-dark-light',
  },
  {
    badge: 'Haulage & Logistics · GPS-Tracked Fleet',
    headlineTop: 'Moving Nigeria',
    headlineBottom: 'Safely. On Time.',
    description: 'GPS-tracked tankers and cargo fleet across all six geopolitical zones — 24/7 dispatch centre, DPR-compliant, always on schedule.',
    ctaPrimary: { label: 'Logistics Services', to: '/services' },
    ctaSecondary: { label: 'Our Coverage', to: '/infrastructure' },
    accentClass: 'text-martimma-yellow',
    badgeBg: 'bg-martimma-yellow/10',
    badgeBorder: 'border-martimma-yellow/20',
    dotPulse: 'bg-martimma-yellow',
    image: '/images/logistics-hero.jpg',
    imageAlt: 'GPS-tracked haulage and logistics fleet',
    placeholderGrad: 'from-[#131000] via-martimma-dark to-martimma-dark-light',
  },
]

const contentVariants = {
  enter: { opacity: 0, y: 24 },
  center: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] } },
  exit: { opacity: 0, y: -16, transition: { duration: 0.35 } },
}

export default function Home() {
  const [current, setCurrent] = useState(0)
  const [paused, setPaused] = useState(false)

  const goTo = useCallback((index: number) => {
    setCurrent(index)
  }, [])

  const next = useCallback(() => {
    goTo((current + 1) % heroSlides.length)
  }, [current, goTo])

  const prev = useCallback(() => {
    goTo((current - 1 + heroSlides.length) % heroSlides.length)
  }, [current, goTo])

  useEffect(() => {
    if (paused) return
    const id = setInterval(next, 5000)
    return () => clearInterval(id)
  }, [paused, next])

  const slide = heroSlides[current]

  return (
    <>
      {/* Hero Section */}
      <section
        className="relative min-h-screen flex flex-col justify-center overflow-hidden"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {/* ── Background: crossfade between slide images ── */}
        <AnimatePresence mode="sync">
          <motion.div
            key={`bg-${current}`}
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2, ease: 'easeInOut' }}
          >
            {/* Gradient placeholder — visible when image hasn't loaded or doesn't exist */}
            <div className={`absolute inset-0 bg-gradient-to-br ${slide.placeholderGrad}`} />
            {/* Actual image */}
            <img
              src={slide.image}
              alt={slide.imageAlt}
              className="absolute inset-0 w-full h-full object-cover"
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
            />
          </motion.div>
        </AnimatePresence>

        {/* ── Fixed overlays (always on top of the image) ── */}
        {/* Dark base so text is always readable */}
        <div className="absolute inset-0 bg-martimma-dark/55" />
        {/* Stronger darkening at top (nav area) and bottom (controls) */}
        <div className="absolute inset-0 bg-gradient-to-b from-martimma-dark/70 via-transparent to-martimma-dark/80" />
        {/* Subtle grid texture */}
        <div className="absolute inset-0 grid-pattern opacity-20" />

        {/* ── Slide content — centered ── */}
        <div className="relative z-10 section-padding max-w-4xl mx-auto w-full text-center pt-28 pb-8">

          <AnimatePresence mode="wait">
            <motion.div
              key={`content-${current}`}
              variants={contentVariants}
              initial="enter"
              animate="center"
              exit="exit"
            >
              {/* Badge */}
              <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${slide.badgeBg} border ${slide.badgeBorder} ${slide.accentClass} text-sm font-medium mb-8 backdrop-blur-sm`}>
                <span className={`w-2 h-2 rounded-full ${slide.dotPulse} animate-pulse`} />
                {slide.badge}
              </span>

              {/* Headline */}
              <h1 className="heading-xl mb-6">
                <span className="text-white">{slide.headlineTop}</span>
                <br />
                <span className="text-gradient">{slide.headlineBottom}</span>
              </h1>

              {/* Description */}
              <p className="body-lg max-w-2xl mx-auto mb-10">
                {slide.description}
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to={slide.ctaPrimary.to} className="btn-primary flex items-center gap-2">
                  {slide.ctaPrimary.label}
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link to={slide.ctaSecondary.to} className="btn-outline">
                  {slide.ctaSecondary.label}
                </Link>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* ── Controls ── */}
          <div className="flex items-center justify-center gap-5 mt-14">
            {/* Prev */}
            <button
              onClick={prev}
              aria-label="Previous slide"
              className="w-10 h-10 rounded-full border border-white/20 bg-black/20 backdrop-blur-sm flex items-center justify-center text-white/60 hover:text-white hover:border-white/40 hover:bg-black/40 transition-all"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            {/* Dots */}
            <div className="flex items-center gap-3">
              {heroSlides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  aria-label={`Go to slide ${i + 1}`}
                  className={`transition-all duration-300 rounded-full ${
                    i === current
                      ? `w-8 h-2 ${slide.dotPulse}`
                      : 'w-2 h-2 bg-white/30 hover:bg-white/50'
                  }`}
                />
              ))}
            </div>

            {/* Next */}
            <button
              onClick={next}
              aria-label="Next slide"
              className="w-10 h-10 rounded-full border border-white/20 bg-black/20 backdrop-blur-sm flex items-center justify-center text-white/60 hover:text-white hover:border-white/40 hover:bg-black/40 transition-all"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Progress bar */}
          {!paused && (
            <div className="mt-4 max-w-[180px] mx-auto h-px bg-white/15 rounded-full overflow-hidden">
              <motion.div
                key={current}
                className={`h-full ${slide.dotPulse}`}
                initial={{ width: '0%' }}
                animate={{ width: '100%' }}
                transition={{ duration: 5, ease: 'linear' }}
              />
            </div>
          )}
        </div>

        {/* ── Stats Bar ── */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="relative z-10 section-padding max-w-7xl mx-auto w-full pb-16"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { value: company.stats.employees, label: 'Skilled Workforce' },
              { value: company.stats.zones, label: 'Coverage Area' },
              { value: company.stats.localContent, label: 'Nigerian Content' },
              { value: company.founded.toString(), label: 'Year Established' },
            ].map((stat, i) => (
              <div key={i} className="glass-card p-5 text-center">
                <div className="text-3xl md:text-4xl font-bold text-martimma-yellow mb-1">{stat.value}</div>
                <div className="text-gray-400 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-5 left-1/2 -translate-x-1/2 z-10"
        >
          <div className="w-6 h-10 rounded-full border-2 border-white/20 flex justify-center pt-2">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1.5 h-1.5 rounded-full bg-martimma-red"
            />
          </div>
        </motion.div>
      </section>

      {/* Services Preview */}
      <section className="py-24 relative">
        <div className="section-padding max-w-7xl mx-auto">
          <AnimatedSection className="text-center mb-16">
            <span className="text-martimma-red text-sm font-semibold tracking-wider uppercase">What We Do</span>
            <h2 className="heading-lg mt-4 mb-6">Four Sectors. One Integrated Vision.</h2>
            <p className="body-lg max-w-2xl mx-auto">
              From petroleum supply to construction, agriculture to logistics — we deliver integrated solutions that power Nigeria's growth.
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon] || Fuel
              return (
                <AnimatedSection key={service.id} delay={index * 0.1}>
                  <Link
                    to="/services"
                    className="group glass-card p-8 hover:border-martimma-red/30 transition-all duration-500 block"
                  >
                    <div className="flex items-start justify-between mb-6">
                      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                        service.color === 'red' ? 'bg-martimma-red/10' : 'bg-martimma-yellow/10'
                      }`}>
                        <IconComponent className={`w-7 h-7 ${
                          service.color === 'red' ? 'text-martimma-red' : 'text-martimma-yellow'
                        }`} />
                      </div>
                      <ChevronRight className="w-6 h-6 text-gray-600 group-hover:text-martimma-yellow group-hover:translate-x-1 transition-all" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">{service.title}</h3>
                    <p className="text-martimma-yellow text-sm font-medium mb-4">{service.subtitle}</p>
                    <p className="text-gray-400 text-sm leading-relaxed">{service.description}</p>
                  </Link>
                </AnimatedSection>
              )
            })}
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 bg-martimma-dark-light relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-martimma-red/30 to-transparent" />

        <div className="section-padding max-w-7xl mx-auto">
          <AnimatedSection className="text-center mb-16">
            <span className="text-martimma-yellow text-sm font-semibold tracking-wider uppercase">Our Foundation</span>
            <h2 className="heading-lg mt-4 mb-6">What We Stand For</h2>
            <p className="body-lg max-w-2xl mx-auto">
              Our TEST IQS+ values guide every decision, every partnership, and every drop we deliver.
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {coreValues.slice(0, 8).map((value, index) => (
              <AnimatedSection key={value.title} delay={index * 0.05}>
                <div className="glass-card p-6 text-center hover:bg-martimma-red/5 transition-colors duration-300 group">
                  <div className="w-12 h-12 rounded-xl bg-martimma-red/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-martimma-red/20 transition-colors">
                    <span className="text-martimma-red font-bold text-lg">{value.title[0]}</span>
                  </div>
                  <h3 className="text-white font-semibold mb-2">{value.title}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">{value.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 relative">
        <div className="section-padding max-w-7xl mx-auto">
          <AnimatedSection className="text-center mb-16">
            <span className="text-martimma-red text-sm font-semibold tracking-wider uppercase">Testimonials</span>
            <h2 className="heading-lg mt-4">Trusted by Industry Leaders</h2>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <AnimatedSection key={index} delay={index * 0.15}>
                <div className="glass-card p-8 h-full flex flex-col">
                  <Quote className="w-8 h-8 text-martimma-red/30 mb-4" />
                  <p className="text-gray-300 leading-relaxed flex-1 mb-6">{testimonial.quote}</p>
                  <div className="border-t border-white/5 pt-4">
                    <p className="text-white font-semibold">{testimonial.author}</p>
                    <p className="text-martimma-yellow text-sm">{testimonial.role}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-martimma-red/20 to-martimma-yellow/20" />
        <div className="absolute inset-0 bg-martimma-dark/80" />

        <div className="relative z-10 section-padding max-w-4xl mx-auto text-center">
          <AnimatedSection>
            <h2 className="heading-lg mb-6">Ready to Power Your Operations?</h2>
            <p className="body-lg mb-10">
              Partner with Martimma for reliable energy supply, construction excellence, agricultural solutions, and logistics services across Nigeria.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/contact" className="btn-primary flex items-center gap-2">
                Start a Conversation
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link to="/about" className="btn-outline">
                Learn More About Us
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </>
  )
}
